import { WhatsappTemplate } from './broadcast';
import { Pagination } from './common';
import { IntegrationType } from './integration';
import { Option } from './rules';

export interface Campaign {
  campaign_id: string;
  account_id: string;
  created_by: string;
  name: string;
  type: string;
  status: CampaignStatus;
  subscribers_context: Record<string, unknown>;
  schedule: { start_at: string; end_at?: string };
  brain_id: string | null;
  brain_parent_id: string | null;
  brain_version: number;
  senders_count: number;
  subscribers_count: number;
  sent_count: number;
  delivered_count: number;
  failed_count: number;
  read_count: number;
  replied_count: number;
  created: string;
  updated: string;
  metadata?: Record<string, unknown>;
  /** Senders included when fetched with verbose=true */
  senders?: CampaignSender[];
  /** NBA config for payment_recovery campaigns */
  nba_config?: NBAConfigInput;
  is_agentic?: boolean;
}

export type CampaignStatus = 'draft' | 'ready' | 'in_progress' | 'sent' | 'failed' | 'canceled';

export type CampaignType = 'marketing' | 'sales' | 'utility' | 'payment_recovery';

export type MetricFormat = 'count' | 'currency' | 'percent';

export interface CampaignFunnelStage {
  key: string;
  value: number;
  /** Share of the previous stage that reached this one. Null on the first. */
  rate: number | null;
}

export interface CampaignMetric {
  key: string;
  /** Null means not measurable, never a measured 0. */
  value: number | null;
  format: MetricFormat;
}

export interface CampaignDailyPtpPoint {
  /** `YYYY-MM-DD`, UTC. */
  date: string;
  /** Debt count, not people. */
  ptp_count: number;
  ptp_amount: number | null;
}

/** `GET /v1/campaigns/:id/analytics`. Only `payment_recovery` returns the full payload. */
export interface CampaignAnalyticsPayload {
  funnel: CampaignFunnelStage[];
  metrics: CampaignMetric[];
  /** Single currency of the campaign's debts; null when mixed, blank, or unknown, so amounts render bare. */
  currency: string | null;
  daily_ptp?: CampaignDailyPtpPoint[];
}

// One duration bucket from the analytics `campaign_call_duration_distribution` function.
// Always four rows ordered 1..4; `percentage` is server-computed and still counts voicemail.
// `voicemail_count` is the voicemail subset of `call_count`. `duration_seconds` and
// `voicemail_duration_seconds` sum the carrier's call seconds (message-time seconds when a call
// has no carrier record), never billed minutes.
export interface CampaignCallDurationBucket {
  bucket_order: number;
  label: string;
  min_seconds: number;
  max_seconds: number | null;
  call_count: number;
  voicemail_count: number;
  percentage: number;
  total_count: number;
  account_id: string;
  duration_seconds: number;
  voicemail_duration_seconds: number;
}

// NBA (Next Best Action) config for payment_recovery campaigns
export interface NBAIntegrationMappings {
  whatsapp?: string;
  'infobip-sms'?: string;
  'infobip-rcs'?: string;
  'mstat-viber'?: string;
  email?: string;
  'twilio-voice'?: string;
  'telnyx-voice'?: string;
  'agora-voice'?: string;
}

export type NBACampaignGoal = 'ptp' | 'payment_claim' | 'payment_api' | (string & {});

/**
 * A single debt entry inside `context.debts[]`.
 *
 * The orchestrator enforces that at least one of `debt_external_id`, `amount`,
 * or `original_creditor` is present per entry. `parties` / `role` are explicitly
 * rejected here — co-borrower setup must go through the Debt API.
 *
 * Field rename (2026-04): `external_account_number` was renamed to
 * `debt_external_id` platform-wide. Sepal/Casavetes call this "Debt External ID".
 */
export interface NBADebtSpecs {
  /** Stable caller-owned identifier for the debt (formerly external_account_number). */
  debt_external_id?: string;
  amount?: number;
  currency?: string;
  /** Unix timestamp in milliseconds. */
  due_date?: number;
  original_creditor?: string;
  debt_type?: string;
}

export interface NBAConfigInput {
  integration_mappings: NBAIntegrationMappings;
  goal?: NBACampaignGoal;
  guidelines?: string;
  compliance_profile_id?: string;
  evaluate_first_message?: boolean;
}

export type Campaigns = { campaigns: Campaign[] } & Pagination;

/**
 * Structured subscriber `context` sent to the orchestrator.
 *
 * As of the 2026-04 NBA payload redesign (Orchestrator PR #1839):
 *  - `debt` (singular) is **rejected** at the schema layer with 400; use `debts[]`.
 *  - Top-level `debt` and `residence_state` are **removed** from subscriber root —
 *    both move inside `context`.
 *  - `contact_id` is an optional cross-tenant shortcut; mismatched tenant → 404.
 *  - `alternate_phones` / `alternate_emails` max 10 each, deduped server-side.
 *  - `debts` max 20 entries per subscriber.
 */
export interface SubscriberContext {
  contact_id?: string;
  time_zone?: string;
  /** 2-char uppercase US state (e.g., "CA", "TX"). */
  residence_state?: string;
  /** 2-char uppercase ISO country code. */
  country_code?: string;
  contact_preferences?: Record<string, unknown>;
  alternate_phones?: string[];
  alternate_emails?: string[];
  debts?: NBADebtSpecs[];
  // Free-form custom variables (promo_code, user.display_name, etc.) flow through.
  [key: string]: unknown;
}

export interface NewCampaignSubscriber {
  phone?: string;
  email?: string;
  context?: SubscriberContext;
  metadata?: Record<string, unknown>;
}

/**
 * Subscriber lifecycle state, sourced from orchestrator (`NBALifecycleStatus`)
 * and dialog-service (`SubscriberLifecycleStatus`). `ok` and `pending` are the
 * active states (`pending` is the DB default for a freshly-inserted row, treated
 * equivalent to `ok` by NBA). `stopped`, `success`, `failed` are terminal stops;
 * the rest are non-active states. Backends still treat this as a free-form string
 * in places, so unknown values fall through.
 */
export type SubscriberLifecycleStatus =
  | 'ok'
  | 'pending'
  | 'paused'
  | 'in_dispute'
  | 'ceased'
  | 'wrong_party'
  | 'legal_hold'
  | 'success'
  | 'stopped'
  | 'failed';

export type NextActionState =
  | 'not_started'
  | 'deciding'
  | 'in_conversation'
  | 'scheduled'
  | 'retrying'
  | 'nothing_scheduled'
  | 'on_hold'
  | 'stopped'
  | 'finished'
  | 'managed_by_workflow';

export type NextActionStopReason =
  | 'manual'
  | 'contact_deleted'
  | 'consent_revoked'
  | 'goal_achieved'
  | 'opt_out'
  | 'compliance'
  | 'campaign_ended'
  | 'first_message_failed'
  | 'nba_stop';

/** What the NBA engine plans next for an agentic subscriber. Absent on non-agentic campaigns. */
export interface NextAction {
  state: NextActionState;
  /** Epoch ms. */
  due_at?: number;
  channel?: string;
  stop_reason?: NextActionStopReason;
  /** paused | in_dispute | legal_hold */
  hold_reason?: string;
  decided_at?: number;
  /** Detail endpoint only. */
  reason?: string;
}

export interface CampaignSubscriber extends NewCampaignSubscriber {
  readonly subscriber_id: string;
  readonly campaign_id: string;
  created?: string;
  updated?: string;
  last_sent_at?: string;
  last_delivered_at?: string;
  last_read_at?: string;
  last_replied_at?: string;
  last_failed_at?: string;
  lifecycle_status?: SubscriberLifecycleStatus | (string & {});
  sender_history?: {
    sender_id: string;
    session_id: string | null;
    sent_at: string | null;
    delivered_at: string | null;
    read_at: string | null;
    replied_at: string | null;
    failed_at: string | null;
    status_code: string | null;
  }[];
  next_action?: NextAction;
}

export interface CampaignSubscribers {
  subscribers: CampaignSubscriber[];
  pagination?: { has_more: boolean; next_cursor?: string; total?: number };
}

export type PartialCampaign = Partial<Campaign>;

// Campaign Sender Types
export enum CampaignSenderStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
}

export enum TransitionCondition {
  DELIVERY_FAILED = 'delivery_failed',
  NO_RESPONSE = 'no_response',
}

export interface CampaignTransition {
  condition: TransitionCondition;
  after_minutes: number;
  sender_id?: string;
}

export interface EmailTemplate {
  subject: string;
  body: string;
}

export interface SmsTemplate {
  text: string;
}

/**
 * Known keys of `campaign_senders.metadata`. Every key is optional and absence
 * is meaningful: it means "use the platform default", which the voice pipeline
 * resolves from its own config.
 */
export interface CampaignSenderMetadata {
  type?: string;
  enabled?: boolean;
  nba_followup?: boolean;
  amd_on_machine?: AmdOnMachine;
  first_message_interruptible?: boolean;
  first_message_delay_ms?: number;
}

export interface NewCampaignSender {
  integration_id: string;
  metadata?: CampaignSenderMetadata;
  transitions?: CampaignTransition[];
  template?: Record<string, unknown>;
}

/**
 * Voicemail action, stored in sender metadata as `amd_on_machine`; absence
 * means the platform default (keep the call going).
 */
export type AmdOnMachine = 'hangup' | 'opener_then_hangup';

/**
 * Campaign-level opener settings for voice campaigns. Written to every voice
 * sender so the first call and the NBA follow-ups cannot diverge.
 *
 * Null or undefined means the key stays out of the metadata, so the platform
 * default applies. `null` is what the form writes when a control is reset,
 * since removing a key outright does not re-render the field.
 */
export interface VoiceCallSettings {
  firstMessageInterruptible?: boolean | null;
  firstMessageDelayMs?: number | null;
}

/** Sanity bound shared by the form, the validator and the orchestrator schema. */
export const MAX_FIRST_MESSAGE_DELAY_MS = 5000;

/**
 * What the voice pipeline uses when `first_message_delay_ms` is absent. Mirrored
 * here so the form can show the effective value instead of an empty field; keep
 * it in step with `CAMPAIGN_FIRST_MESSAGE_DELAY_MS` in Channels.
 */
export const DEFAULT_FIRST_MESSAGE_DELAY_MS = 1500;

/**
 * Base sender interface used for both primary and fallback senders
 */
export interface BaseCampaignSender {
  integrationId: string;
  integrationType: IntegrationType;
  content: string;
  template?: WhatsappTemplate;
  // Email-specific properties
  subject?: string;
  // Fallback-specific properties (not used for primary sender)
  waitTimeMinutes?: number;
  // Parameters for WhatsApp templates (each sender can have its own)
  headerParameters?: Array<{ name: Option | string }>;
  bodyParameters?: Array<{ name: Option | string }>;
  footerParameters?: Array<{ name: Option | string }>;
  // Media header parameter for IMAGE, VIDEO, DOCUMENT headers
  headerMediaParam?: { type: 'id' | 'link'; value: string | Option };
  // Voice-specific: what to do when AMD detects an answering machine.
  // Null means the platform default, same as the opener settings.
  amdOnMachine?: AmdOnMachine | null;
}
export interface CampaignSender extends NewCampaignSender {
  readonly sender_id: string;
  readonly campaign_id: string;
  channel?: string;
  integration_type?: string;
  status?: CampaignSenderStatus;
  subscribers_count?: number;
  sent_count?: number;
  delivered_count?: number;
  failed_count?: number;
  read_count?: number;
  replied_count?: number;
  created?: string;
  updated?: string;
}

export interface CampaignSenders {
  campaign_senders: CampaignSender[];
}

export type PartialCampaignSender = Partial<CampaignSender> & { sender_id: string };

// Journey types for payment recovery subscriber journey

export interface JourneySessionMessage {
  author_type: string;
  responses: Array<{ text?: string; type?: string }>;
  timestamp: number;
}

export interface JourneySession {
  session_id: string;
  channel: string;
  started_at: number;
  ended_at?: number;
  messages: JourneySessionMessage[];
  sent_at?: number;
  delivered_at?: number;
  read_at?: number;
  replied_at?: number;
  failed_at?: number;
  has_voicemail?: boolean;
  status_code?: string;
  context_vars?: Record<string, unknown>;
}

/**
 * Campaign-level block codes set by NBA when it stops outreach.
 * Sourced from orchestrator (`NBACampaignBlock`) + dialog-service (`CampaignBlockId`),
 * which currently disagree (`llm_decided_stop` vs `llm_stop`,
 * `channel_not_allowed` vs `no_available_channels`, etc.) — we accept both.
 */
export type NBACampaignBlock =
  | 'budget_exhausted'
  | 'paused_contact'
  | 'llm_decided_stop'
  | 'llm_stop'
  | 'llm_failure'
  | 'channel_not_allowed'
  | 'no_available_channels'
  | 'no_integration_mapping'
  | 'payment_confirmed'
  | 'goal_achieved'
  | 'no_channel_consent';

/**
 * Compliance/regulatory block codes set by NBA when it stops outreach.
 *
 * Source of truth: dialog-service `ComplianceRuleId` enum (nba/src/nba/compliance/types.py)
 * and the canonical list documented by Alexis in MOV-14826 comment.
 *
 * Note: `wrong_party` is not in the current dialog-service enum but has appeared in
 * historical `compliance_blocks` payloads; kept for backward compatibility.
 */
export type NBAComplianceBlock =
  | 'opt_out'
  | 'channel_opt_out'
  | 'cease_desist'
  | 'refusal_to_pay'
  | 'dispute'
  | 'identity_theft'
  | 'coerced_debt'
  | 'bankruptcy'
  | 'attorney_representation'
  | 'deceased'
  | 'wrong_party'
  | 'scra_protection'
  | 'overdue_grace_period'
  | 'public_holidays'
  | 'consent'
  | 'inconvenient_contact'
  | 'quiet_hours'
  | 'quiet_hours_br'
  | 'quiet_hours_br_rj'
  | 'quiet_hours_br_es'
  | 'quiet_hours_br_pr'
  | 'quiet_hours_br_am'
  | 'frequency_capping'
  | 'frequency_capping_ca'
  | 'frequency_capping_ma'
  | 'frequency_capping_gr'
  | 'frequency_capping_br';

export interface NBAAction {
  label: 'follow_up_later' | 'follow_up_now' | 'stop';
  reason?: string;
  schedule_at?: number;
  reminder_seconds?: number;
  channel?: string;
  campaign_blocks?: Array<NBACampaignBlock | (string & {})>;
  compliance_blocks?: Array<NBAComplianceBlock | (string & {})>;
}

export interface NBAOutput {
  action?: NBAAction;
  trigger_source?: string;
  timestamp: number;
  status?: 'completed' | 'failed' | (string & {});
  /**
   * Failure category (e.g. 'TimeoutError', 'DecisionLayerError') set when `status === 'failed'`.
   * Currently only populated once the NBA service threads it through (see MOV-16650); the UI
   * degrades gracefully to a generic message when absent.
   */
  error_type?: string;
  /** Raw failure message. May contain PII/provider text — do not render verbatim. */
  error?: string;
}

/** A stop or resume that did not come from the NBA. `reason: 'manual'` means a user did it. */
export interface JourneyStatusChange {
  action: 'stopped' | 'resumed';
  reason: 'manual' | 'contact_deleted' | 'consent_revoked' | (string & {});
  previous_status: string | null;
  actor_user_id: string | null;
  timestamp: number;
}

export type ContactJourneyEntry =
  | { session: JourneySession; nba_output?: never; status_change?: never }
  | { nba_output: NBAOutput; session?: never; status_change?: never }
  | { status_change: JourneyStatusChange; session?: never; nba_output?: never };

export interface CampaignSubscriberWithJourney extends CampaignSubscriber {
  contact_journey: ContactJourneyEntry[];
}
